import React from "react";
import Layout from "../../components/base/blog-layout";
import SEO from "../../components/base/seo";
import headerImage from "../../images/blog/TSD_Wrapping_Header.jpg";
import pageImage from "../../images/blog/TSD_Wrapping_Image.jpg";


const BlogPost2023MarketingPlan = () => (
<Layout>
<SEO
    title="10 Tips to Ride Out a Recession as a Small Business Owner"
    description="With 2022 wrapping up, it’s time to focus on 2023 marketing planning! Here is our simple guide to setting yourself up for a successful year."
/>

{/* Header image and top info section */}
<main className="template-resources">
    <div className="single-resource-post">
    <section className="page-heading">
        <div className="container">
        <div
className="single-resource-header-image"
style={{ backgroundImage: `url(${headerImage})` }}
></div>
        </div>
    </section>

    <section>
        <p className="single-resource-top-info container">
        The Simple Department | December 19, 2022
        </p>

        <div className="container blog-content-wrapper">
        <h1>Your Simple Guide to a 2023 Marketing Plan</h1>

        <p>
            As we’re wrapping up 2022 (and wrapping our holiday gifts!),
            thoughts naturally turn to our goals, hopes, and dreams for 2023.
            When it comes to 2023 marketing planning, taking a look at the
            progress you’ve made in 2022 and setting out a SMART (specific,
            measurable, achievable, relevant, and time-bound) plan for reaching
            your 2023 goals should be top of your end of year list. This can
            seem daunting – especially when you’d rather be relaxing with some
            eggnog – but we have some tips and simple steps to get you started.
        </p>

        <h2>Evaluate Last Year</h2>
        <p>
            Now is the time to revisit 2022’s goals and really evaluate your
            results. Hopefully, you’ve been monitoring this throughout the year,
            but now is the time to get a complete picture of the year’s
            progress. Take a close look at the goals you set for 2022 and do a
            deep dive into what you did to meet those goals, what worked, and
            what didn’t work.
        </p>

        <p><strong>Here are a few things to pay particular attention to:</strong></p>
        <ul>
            <li>
            How much has your company changed/grown in the last year? What
            does this mean for your goals/strategy next year?
            </li>
            <li>
            What channels really drove value for you in a meaningful way last
            year?
            </li>
            <li>
            Are there any trends in customer feedback over the past year?
            What can you learn from that?
            </li>
        </ul>

        <h2>Take Advantage of Reporting Tools</h2>
        <p>
            At The Simple Department, we love a good report. We’re just cool
            like that. Monitoring your own performance through regular
            reporting is crucial. A report that can give you insight into what
            your competitors are doing and how it’s working is icing on the
            reporting cake (delicious!). That’s why we offer (free!) custom
            reports that offer insights into your competitors’ marketing
            performance and how you stack up. Consider it a holiday gift from us
            to you.
        </p>

        <p>
            <a href="/freecreatorinsightreport/">- The Signals Report</a>
            </p>
            <p>This report provides you with unique insights into what your competitors are doing well when it comes to social content, giving you the inspiration you need to come up effective creator campaigns and engaging social content that drives real results for your company.</p>

            <p>
            <a href="/freesocialmedialisteningreport/">- The Explore Report</a>
        </p>
        <p>The Explore report is a social listening report, meaning it allows you to check on your brand, competitors, and industry media coverage. You can benchmark your performance against competitors’ PR, SEO, and social media activities, as well as gauge customer sentiment – aka how your audience perceives your brand in relation to competitors and the industry.</p>

        <h2>Get 2023 Marketing Planning Started with SMART Goals</h2>
        <p>
            You’ve looked at your 2022 performance. You’ve looked at your
            competitors. Now it’s time to really dig into your 2023 marketing
            plan. If you weren’t using the SMART goals framework last year,
            you’re likely kicking yourself now, because your 2022 goals were
            poorly defined, hard to measure, or too ambitious. SMART goals are
            Specific, Measurable, Achievable, Relevant, and Time-bound.
        </p>
        <p>
            Thinking about these parameters during yearly planning helps you
            make sure that you’re setting goals you can actually reach in a
            year, eliminates the risk of setting vague goals, and makes
            tracking progress much simpler.
        </p>

        <p><strong>Here’s an example of what a SMART goal looks like:</strong></p>
        <p>
            <i>
            Our goal is to [quantifiable objective] by [timeframe or
            deadline]. [Key players or teams] will accomplish this goal by
            [what steps you’ll take to achieve the goal]. Accomplishing this
            goal will [result or benefit].
            </i>
        </p>
        <p>
            <i>
            <strong>For example, our Creative Director’s holiday goal is:</strong>
            My goal is to stay sane until January 3rd. I will accomplish this
            by taking frequent walks alone, stress-baking gingerbread, and
            hiding my toddler’s advent calendar for a couple days when I need
            some extra sugar. Accomplishing this goal will result in happy
            holiday memories and not overspending on Boxing Day.
            </i>
        </p>
        <img
style={{ width: "100%", margin: "10px 0" }}
src={pageImage}
alt="Wrapping gifts"
/>

        <h2>Make Sure You’re Tracking the Right Metrics</h2>
        <p>
            Setting SMART goals should help you ensure that you are setting
            goals you can measure – but choosing how to measure your goals is
            also key. Deciphering what metrics really relate to what goals can
            be tricky, and it’s easy to fall into the trap of paying attention
            to vanity metrics.
        </p>

        <h3>What is a Vanity Metric?</h3>
        <p>
            In essence, there are two types of metrics – vanity metrics and
            actionable metrics. Actionable metrics provide actionable
            information about business performance, whereas vanity metrics
            might make you look good, but don’t reflect real performance, and
            don’t provide any data that informs marketing strategy. Marketing
            agencies are notorious for relying on vanity metrics because they’re
            exciting to share and easy to get.
        </p>

        <h2>Dig Into The Data</h2>
        <p>
            When looking at your data, ask yourself: how does this number relate
            to your actual goals? If you’re looking at an increase in social
            media followers (a classic vanity metric), are you also seeing an
            increase in traffic to your website from social media? And are those
            social media referrals leading to purchases? How much actual revenue
            can be tracked back to social media?
        </p>
        <p>
            Any metric can be a vanity metric if it’s surface level and not
            relevant to your goals. But conversely, vanity metrics can become
            actionable metrics with a little more digging. All you need to do is
            stay focused on your goals, and search for the context and nuance in
            your data.
        </p>

        <h3>Start Your 2023 Marketing Planning With the Simple Department!</h3>
        <p>
            No matter what your 2023 marketing planning looks like or what your
            objectives are, we have one main piece of advice – keep it simple!
            Agencies can make setting and achieving your marketing goals seem
            complicated and difficult with their unnecessary processes,
            pointless ego trips, and general noise. That’s how they stay in
            business! The Simple Department is different. We think marketing
            relationships are at their best when they’re simple, and we love
            using our knowledge, experience, and capabilities to help you get
            awesome marketing stuff done.
        </p>

        <p>
            <a className="link-darkblue" href="/contact">
            If you need help with any aspect of your marketing plan, let us
            know!
            </a>
        </p>

        <div>
            <a href="/blog" className="back-btn-resource">
            back
            </a>
        </div>
        </div>
    </section>
    </div>
</main>
</Layout>
);

export default BlogPost2023MarketingPlan;
